import { useCascadeStore } from '../store'
// data imports removed — vendor names now stored as text on node
import { NODE_COLORS, FUZZINESS_CONFIG } from '../types'
// lucide-react icons removed — no longer needed in overview panel
import { NodeDetailPanel } from './NodeDetailPanel'
import { ValidationDetailPanel } from './ValidationDetailPanel'

export function InfoPanel() {
  const { nodes, edges, ruleset, ruleDescription, country, countryCode, countryCascades, removeCountry, notes, setNotes, selectedNodeId, updateNode, setNodeNotes } = useCascadeStore()
  const hasMultipleCountries = (countryCode ? 1 : 0) + countryCascades.length > 1

  // Find selected node
  const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) : null
  const showCustomerEditor = selectedNode?.type === 'customer-input'
  const showCaptureEditor = selectedNode?.type === 'capture-method' && selectedNode.collectedFields
  const showValidationEditor = selectedNode?.type === 'validation'

  // Get validation nodes
  const validationNodes = nodes.filter(n => n.type === 'validation')

  // Get capture-method nodes that have configured fields
  const captureNodes = nodes.filter(n => n.type === 'capture-method' && n.collectedFields)

  return (
    <div className="w-72 shrink-0 bg-[var(--bg-secondary)] border-l border-[var(--border-color)] overflow-y-auto">
      <div className="p-4 space-y-5">

        {/* Node detail editor */}
        {showCustomerEditor && selectedNode ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">👤</span>
              <div>
                <h3 className="text-xs font-bold text-[var(--text-primary)]">Customer</h3>
                <p className="text-[10px] text-[var(--text-muted)]">How the customer enters the process</p>
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
                Entry Method
              </span>
              <input
                type="text"
                value={selectedNode.sublabel ?? ''}
                onChange={(e) => updateNode(selectedNode.id, { sublabel: e.target.value })}
                placeholder="e.g. Provides their details, Sends an email, Contacts via broker..."
                className="w-full text-[11px] px-2.5 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
                Notes
              </span>
              <textarea
                value={selectedNode.nodeNotes ?? ''}
                onChange={(e) => setNodeNotes(selectedNode.id, e.target.value)}
                placeholder="e.g. Customer applies online via the client's website..."
                className="w-full text-[11px] p-2.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none leading-relaxed"
                rows={3}
              />
            </div>
          </div>
        ) : showCaptureEditor && selectedNode ? (
          <NodeDetailPanel node={selectedNode} />
        ) : showValidationEditor && selectedNode ? (
          <ValidationDetailPanel node={selectedNode} />
        ) : selectedNode ? (
          <>
            {/* Selected node info + notes */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">{(NODE_COLORS[selectedNode.type] || NODE_COLORS['validation']).icon}</span>
                <div>
                  <h3 className="text-xs font-bold text-[var(--text-primary)]">{selectedNode.label}</h3>
                  {selectedNode.sublabel && (
                    <p className="text-[10px] text-[var(--text-muted)]">{selectedNode.sublabel}</p>
                  )}
                  {selectedNode.vendor && (
                    <p className="text-[10px] text-[var(--text-muted)]">{selectedNode.vendor}</p>
                  )}
                </div>
              </div>
              {selectedNode.description && (
                <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">{selectedNode.description}</p>
              )}
              <div>
                <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
                  Notes
                </span>
                <textarea
                  value={selectedNode.nodeNotes ?? ''}
                  onChange={(e) => setNodeNotes(selectedNode.id, e.target.value)}
                  placeholder="Add notes about this step..."
                  className="w-full text-[11px] p-2.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none leading-relaxed"
                  rows={3}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Rule summary */}
            {ruleset && (
              <div>
                <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
                  Rule: {ruleDescription || ruleset}
                </h3>
                {country && (
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {country} ({countryCode})
                  </div>
                )}
              </div>
            )}

            {/* Capture methods & their fields */}
            {captureNodes.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-2">
                  Data Captured
                </h3>
                <div className="space-y-2">
                  {captureNodes.map(cn => {
                    const enabled = cn.collectedFields?.filter(f => f.enabled) ?? []
                    if (enabled.length === 0) return (
                      <button
                        key={cn.id}
                        onClick={() => useCascadeStore.getState().selectNode(cn.id)}
                        className="w-full text-left px-2.5 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-dashed border-[var(--border-color)] hover:border-blue-400 transition-colors"
                      >
                        <div className="text-[10px] text-[var(--text-muted)]">
                          {(NODE_COLORS[cn.type] || NODE_COLORS['validation']).icon} {cn.label}
                        </div>
                      </button>
                    )
                    return (
                      <button
                        key={cn.id}
                        onClick={() => useCascadeStore.getState().selectNode(cn.id)}
                        className="w-full text-left px-2.5 py-2 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] transition-colors"
                      >
                        <div className="text-[10px] font-semibold text-[var(--text-primary)] mb-1">
                          {(NODE_COLORS[cn.type] || NODE_COLORS['validation']).icon} {cn.label}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {enabled.map(f => (
                            <span key={f.id} className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${(FUZZINESS_CONFIG[f.fuzziness] || FUZZINESS_CONFIG['moderate']).bg} ${(FUZZINESS_CONFIG[f.fuzziness] || FUZZINESS_CONFIG['moderate']).color}`}>
                              {f.label}
                            </span>
                          ))}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Validation steps */}
            {validationNodes.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
                  Validation Steps
                </h3>
                <div className="space-y-2">
                  {validationNodes.map(vn => (
                      <button
                        key={vn.id}
                        onClick={() => useCascadeStore.getState().selectNode(vn.id)}
                        className="w-full text-left px-2.5 py-2 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] transition-colors"
                      >
                        <div className="text-[10px] font-semibold text-[var(--text-primary)] mb-1">
                          🛡️ {vn.label}
                          {vn.validationRuleset && (
                            <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-medium">
                              {vn.validationRuleset}
                            </span>
                          )}
                        </div>
                        {vn.validationVendor && (
                          <div className="text-[9px] text-[var(--text-secondary)]">{vn.validationVendor}</div>
                        )}
                      </button>
                  ))}
                </div>
              </div>
            )}

            {/* Flow summary */}
            <div>
              <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-2">
                Cascade Summary
              </h3>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div className="px-2 py-1.5 rounded-md bg-[var(--bg-tertiary)]">
                  <div className="text-[var(--text-muted)]">Validations</div>
                  <div className="text-sm font-bold text-[var(--text-primary)]">{validationNodes.length}</div>
                </div>
                <div className="px-2 py-1.5 rounded-md bg-[var(--bg-tertiary)]">
                  <div className="text-[var(--text-muted)]">Connections</div>
                  <div className="text-sm font-bold text-[var(--text-primary)]">{edges.length}</div>
                </div>
                <div className="px-2 py-1.5 rounded-md bg-[var(--bg-tertiary)]">
                  <div className="text-[var(--text-muted)]">AML</div>
                  <div className="text-sm font-bold text-[var(--text-primary)]">{nodes.filter(n => n.type === 'aml-screening').length > 0 ? 'Yes' : 'No'}</div>
                </div>
                <div className="px-2 py-1.5 rounded-md bg-[var(--bg-tertiary)]">
                  <div className="text-[var(--text-muted)]">Captures</div>
                  <div className="text-sm font-bold text-[var(--text-primary)]">{captureNodes.length}</div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-2">Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Cascade dependencies, known issues, etc."
                className="w-full text-[11px] p-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                rows={4}
              />
            </div>

            {/* Remove country (only when multiple countries exist) */}
            {hasMultipleCountries && countryCode && (
              <button
                onClick={() => removeCountry(countryCode)}
                className="w-full text-[10px] py-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/5 transition-colors"
              >
                Remove {country} cascade
              </button>
            )}
          </>
        )}

      </div>
    </div>
  )
}
